// Turns pointer input and shop actions into commands. Owns selection and placement mode.
import { Container, Graphics, type FederatedPointerEvent } from 'pixi.js';
import type { Balance, Command, Id, MapData, World } from '../sim';
import { isBuildable, plantPlacementError, substationRadius } from '../sim';
import { drawPlantGlyph, type Pick } from '../render/entities';
import { palette } from '../render/palette';
import type { ShopAction } from './shop';

export type Mode =
  { kind: 'idle' } | { kind: 'place'; what: 'substation' | 'thermal' } | { kind: 'move'; id: Id };

export interface Controller {
  ghost: Container;
  mode: Mode;
  selected: Pick | null;
  modeName(): string;
  hint(world: World): string;
  onPick(pick: Pick, world: World): void;
  onShop(action: ShopAction): void;
  onMapTap(x: number, y: number, world: World): void;
  onMapMove(x: number, y: number, world: World): void;
  onEscape(): void;
}

export function createController(
  map: MapData,
  balance: Balance,
  emit: (c: Command) => boolean,
): Controller {
  const ghost = new Container();
  const ghostG = new Graphics();
  const ghostLine = new Graphics();
  ghost.addChild(ghostLine, ghostG);
  ghost.visible = false;
  ghost.eventMode = 'none';
  let pointer = { x: 0, y: 0 };
  const seenHints = new Set<string>();

  const c: Controller = {
    ghost,
    mode: { kind: 'idle' },
    selected: null,
    modeName: () => c.mode.kind,

    hint(world) {
      let text = '';
      let key = '';
      if (c.mode.kind === 'place') {
        key = 'place';
        text = 'Tap the map to place it. Esc to cancel.';
      } else if (c.mode.kind === 'move') {
        key = 'move';
        text = 'Tap where the substation should go.';
      } else if (c.selected?.kind === 'plant') {
        key = 'tap';
        text = 'Now tap a substation to run a line to it.';
      } else if (c.selected?.kind === 'substation') {
        key = 'sub';
        text =
          'A substation lights every home inside its circle, as far as its transformer allows.';
      } else if (c.selected?.kind === 'tap') {
        key = 'line';
        text = 'Longer lines lose more power. High voltage fixes most of that.';
      } else if (world.time.day === 1 && world.time.hour < 12) {
        key = 'start';
        text = 'Keep every home lit. Tap a plant, then a substation, to connect them.';
      }
      if (!key) return '';
      if (seenHints.has(key) && key !== 'tap') return '';
      return text;
    },

    onPick(pick, world) {
      if (c.mode.kind !== 'idle') return;
      if (c.selected?.kind === 'plant' && pick.kind === 'substation') {
        if (emit({ t: 'Tap', plantId: c.selected.id, substationId: pick.id })) {
          const plant = world.plants.find((p) => p.id === c.selected?.id);
          const used = world.taps.filter((t) => t.plantId === plant?.id).length;
          if (!plant || used >= plant.maxTaps) c.selected = null;
        }
        return;
      }
      c.selected = c.selected?.id === pick.id ? null : pick;
      if (c.selected)
        seenHints.add(
          c.selected.kind === 'plant' ? 'tap' : c.selected.kind === 'substation' ? 'sub' : 'line',
        );
    },

    onShop(action) {
      switch (action.t) {
        case 'place':
          c.mode = { kind: 'place', what: action.what };
          c.selected = null;
          seenHints.add('place');
          break;
        case 'move':
          c.mode = { kind: 'move', id: action.id };
          seenHints.add('move');
          break;
        case 'upgradeTransformer':
          emit({ t: 'UpgradeTransformer', id: action.id });
          break;
        case 'upgradeLine':
          emit({ t: 'UpgradeLine', tapId: action.id });
          break;
        case 'untap':
          emit({ t: 'Untap', tapId: action.id });
          c.selected = null;
          break;
        case 'cancel':
          c.mode = { kind: 'idle' };
          c.selected = null;
          break;
      }
      ghost.visible = c.mode.kind !== 'idle';
      redrawGhost(null);
    },

    onMapTap(x, y, world) {
      const xi = Math.round(x);
      const yi = Math.round(y);
      if (c.mode.kind === 'place') {
        const ok =
          c.mode.what === 'substation'
            ? emit({ t: 'PlaceSubstation', x: xi, y: yi })
            : emit({ t: 'PlacePlant', type: 'thermal', x: xi, y: yi });
        if (ok) {
          c.mode = { kind: 'idle' };
          ghost.visible = false;
        }
        return;
      }
      if (c.mode.kind === 'move') {
        if (emit({ t: 'MoveSubstation', id: c.mode.id, x: xi, y: yi })) {
          c.mode = { kind: 'idle' };
          ghost.visible = false;
        }
        return;
      }
      c.selected = null;
      void world;
    },

    onMapMove(x, y, world) {
      pointer = { x, y };
      if (c.mode.kind === 'idle' && c.selected?.kind === 'plant') {
        const plant = world.plants.find((p) => p.id === c.selected?.id);
        ghost.visible = !!plant;
        ghostG.clear();
        ghostLine.clear();
        if (plant) {
          ghostLine
            .moveTo(plant.x, plant.y)
            .lineTo(x, y)
            .stroke({ color: palette.select, width: 1.5, alpha: 0.35 });
        }
        return;
      }
      if (c.mode.kind === 'idle') {
        ghost.visible = false;
        return;
      }
      ghost.visible = true;
      redrawGhost(world);
    },

    onEscape() {
      c.mode = { kind: 'idle' };
      c.selected = null;
      ghost.visible = false;
    },
  };

  function redrawGhost(world: World | null): void {
    ghostLine.clear();
    ghostG.clear();
    ghostG.position.set(pointer.x, pointer.y);
    const inside = isBuildable(pointer.x, pointer.y, map);
    if (c.mode.kind === 'place' && c.mode.what === 'thermal') {
      const bad = world
        ? plantPlacementError(world, map, balance, 'thermal', pointer.x, pointer.y)
        : !inside;
      drawPlantGlyph(ghostG, 'thermal');
      ghostG.alpha = 0.55;
      ghostG.tint = bad ? palette.bad : 0xffffff;
      return;
    }
    const r = substationRadius(map, balance);
    ghostG.circle(0, 0, r).fill({ color: inside ? palette.select : palette.bad, alpha: 0.08 });
    ghostG
      .circle(0, 0, r)
      .stroke({ color: inside ? palette.select : palette.bad, width: 1, alpha: 0.5 });
    ghostG.circle(0, 0, 9).fill({ color: palette.land, alpha: 0.9 });
    ghostG.circle(0, 0, 9).stroke({ color: inside ? palette.select : palette.bad, width: 2 });
    ghostG.alpha = 0.8;
    ghostG.tint = 0xffffff;
  }

  return c;
}

export function attachPointer(
  stage: Container,
  mapLayer: Container,
  onTap: (x: number, y: number) => void,
  onMove: (x: number, y: number) => void,
): void {
  stage.eventMode = 'static';
  stage.on('pointertap', (e: FederatedPointerEvent) => {
    const p = mapLayer.toLocal(e.global);
    onTap(p.x, p.y);
  });
  stage.on('pointermove', (e: FederatedPointerEvent) => {
    const p = mapLayer.toLocal(e.global);
    onMove(p.x, p.y);
  });
}
